import type {Href} from "expo-router";

// A destination that arrived before the app could go there.
//
// The case this exists for: a push tapped while nobody is logged in. The response listener lives
// above the auth gate, and <Authenticated> answers an unauthenticated render with
// `<Redirect href="/login"/>` — which replaces whatever was pushed, so the destination is simply
// lost. That is a latent bug for EVERY deep link this app will ever grow, not only notifications,
// which is why the stash is spelled in terms of an `Href` and knows nothing about notifications.
//
// Module-level state rather than React state, for three reasons and not for brevity:
//   1. The writer is not in a render. A notification response is delivered to a plain callback,
//      and on a cold start `getLastNotificationResponseAsync` resolves before there is any
//      component that could hold the value.
//   2. It has to survive the tree that would hold it. Redirecting to /login unmounts the entire
//      `(app)` group; state parked in there goes with it, and state parked above it would make
//      the whole app re-render for a value nothing renders.
//   3. Nothing displays it. A context would publish a value with no readers except the one
//      component that consumes and immediately discards it.
//
// It is per-process, so an app that is killed before login forgets — correct: a deep link the user
// never completed is not owed to them a day later.

let pendingRoute: Href | null = null;

/**
 * Remember where the app should go once it can.
 *
 * Last one wins. Two taps before login is a user who changed their mind, and landing them on the
 * older of the two would be the wrong half of the choice.
 */
export function setPendingRoute(href: Href): void {
    pendingRoute = href;
}

/**
 * Take the pending destination, if there is one, and forget it in the same step.
 *
 * Read-and-clear together is the whole point. A pending route that survives being read is a route
 * that fires again on the next login — or twice on a remount — so the once-ness lives here, in
 * the store, rather than in a flag the caller has to remember to set.
 */
export function consumePendingRoute(): Href | null {
    const href = pendingRoute;
    pendingRoute = null;
    return href;
}
